// @ts-nocheck
interface NotificationData {
  id: string;
  type: 'appointment' | 'task' | 'payment' | 'document' | 'case' | 'reminder' | 'deadline';
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isRead: boolean;
  createdAt: string;
  scheduledFor?: string;
  relatedId?: string;
  relatedType?: string;
  icon?: string;
  actionUrl?: string;
}

interface ScheduledNotification {
  id: string;
  type: 'appointment' | 'task' | 'deadline' | 'reminder';
  title: string;
  message: string;
  scheduledFor: Date;
  originalDate: Date;
  relatedId: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isRecurring?: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly';
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private isSupported: boolean;
  private scheduledNotifications: Map<string, ScheduledNotification> = new Map();
  private timeouts: Map<string, NodeJS.Timeout> = new Map();

  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    this.permission = this.isSupported ? Notification.permission : 'denied';
    this.initializeService();
  }

  private async initializeService(): Promise<void> {
    if (!this.isSupported) {
      console.warn('Notificações não são suportadas neste dispositivo');
      return;
    }

    // Registrar service worker se não estiver registrado
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        console.log('Service Worker registrado para notificações:', registration);
      } catch (error) {
        console.error('Erro ao registrar Service Worker:', error);
      }
    }
  }

  async requestPermission(): Promise<boolean> {
    console.log('[NotificationService] 🔐 Solicitando permissão de notificação...');
    
    if (!this.isSupported) {
      console.warn('[NotificationService] ❌ Notificações não são suportadas neste navegador');
      return false;
    }

    // Verificação específica para iOS
    if (this.isIOSDevice()) {
      console.log('[NotificationService] 📱 Dispositivo iOS detectado');
      
      if (!this.isStandalonePWA()) {
        console.warn('[NotificationService] ⚠️ iOS: PWA não foi adicionado à tela inicial');
        console.warn('[NotificationService] 💡 Para receber notificações no iOS:');
        console.warn('[NotificationService] 1. Abra este site no Safari');
        console.warn('[NotificationService] 2. Toque no botão "Compartilhar" (ícone de seta)');
        console.warn('[NotificationService] 3. Selecione "Adicionar à Tela de Início"');
        console.warn('[NotificationService] 4. Abra o app pela tela inicial');
        
        return false;
      }
      
      if (!this.isIOSPWACapable()) {
        console.warn('[NotificationService] ❌ iOS: Versão não suporta notificações PWA (necessário iOS 16.4+)');
        return false;
      }
      
      console.log('[NotificationService] ✅ iOS: PWA configurado corretamente para notificações');
    }

    if (this.permission === 'granted') {
      console.log('[NotificationService] ✅ Permissão já concedida');
      // Já tem permissão, garantir que a subscription está criada
      await this.setupPushSubscription();
      return true;
    }

    if (this.permission === 'denied') {
      // Bloqueadas no navegador: segue sem notificações, sem interromper o usuário com pop-ups
      console.warn('[NotificationService] ❌ Permissão de notificação negada pelo usuário');
      return false;
    }

    try {
      console.log('[NotificationService] 🔔 Solicitando permissão ao usuário...');
      const permission = await Notification.requestPermission();
      console.log('[NotificationService] Resultado da permissão:', permission);
      this.permission = permission;
      
      if (permission === 'granted') {
        // Criar subscription push automaticamente
        await this.setupPushSubscription();
      }
      
      return permission === 'granted';
    } catch (error) {
      console.error('[NotificationService] ❌ Erro ao solicitar permissão de notificação:', error);
      return false;
    }
  }

  private async setupPushSubscription(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[NotificationService] Push não suportado');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      if (!('pushManager' in registration)) {
        console.warn('[NotificationService] PushManager não disponível');
        return;
      }

      // Verificar se já existe subscription
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[NotificationService] ✅ Subscription já existe, enviando ao servidor...');
        try {
          await this.sendSubscriptionToServer(existingSubscription);
          console.log('[NotificationService] ✅ Subscription enviada com sucesso');
        } catch (err) {
          console.warn('[NotificationService] ⚠️ Falha não-crítica ao atualizar subscription:', err);
        }
        return;
      }

      // Obter VAPID public key
      const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!VAPID_PUBLIC_KEY) {
        console.warn('[NotificationService] VAPID_PUBLIC_KEY não configurado');
        return;
      }

      // Converter VAPID key para Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

      // Criar nova subscription
      console.log('[NotificationService] 🔔 Criando subscription push...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      console.log('[NotificationService] ✅ Subscription criada:', subscription);
      
      // Enviar para o servidor
      await this.sendSubscriptionToServer(subscription);
    } catch (error) {
      console.error('[NotificationService] ❌ Erro ao configurar push subscription:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      // Buscar userId do Supabase (com fallback robusto para iOS/PWA)
      let userId: string | null = null;
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: { session } } = await supabase.auth.getSession();
        userId = session?.user?.id || null;
        if (!userId) {
          const { data: { user } } = await supabase.auth.getUser();
          userId = user?.id || null;
        }
      } catch (err) {
        console.warn('[NotificationService] Não foi possível obter userId:', err);
      }

      // Se sessão ainda não hidratou, tenta novamente algumas vezes
      if (!userId) {
        for (let attempt = 0; attempt < 3 && !userId; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 700));
          try {
            const { supabase } = await import('@/integrations/supabase/client');
            const { data: { user } } = await supabase.auth.getUser();
            userId = user?.id || null;
          } catch {
            // ignora e tenta novamente
          }
        }
      }

      const response = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'x-user-id': userId } : {})
        },
        body: JSON.stringify({
          subscription,
          userId
        })
      });

      if (!response.ok) {
        // Se falhar, apenas logamos o aviso mas não lançamos erro para não assustar o usuário
        // já que o service worker vai funcionar localmente de qualquer jeito
        const errorText = await response.text();
        console.warn('[NotificationService] Aviso: Falha ao sincronizar subscription com servidor:', errorText);
        return; 
      }

      console.log('[NotificationService] ✅ Subscription enviada ao servidor com sucesso');
    } catch (error) {
      console.warn('[NotificationService] ⚠️ Aviso: Falha ao enviar subscription ao servidor (modo offline ou erro de rede):', error);
    }
  }

  async showNotification(notification: NotificationData): Promise<void> {
    if (!this.isSupported || this.permission !== 'granted') {
      return;
    }

    try {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/logoasad.png', // Ícone do app
        badge: '/logoasad.png',
        tag: notification.id, // Evita duplicatas
        requireInteraction: notification.priority === 'urgent',
        silent: false,
        data: {
          id: notification.id,
          type: notification.type,
          actionUrl: notification.actionUrl,
          relatedId: notification.relatedId
        }
      });

      // Auto-close para notificações não urgentes após 10 segundos
      if (notification.priority !== 'urgent') {
        setTimeout(() => {
          browserNotification.close();
        }, 10000);
      }

      // Adicionar evento de clique
      browserNotification.onclick = () => {
        browserNotification.close();
        
        // Focar na janela do app se estiver em background
        if (window.parent !== window) {
          window.parent.focus();
        } else {
          window.focus();
        }

        // Navegar para a URL da ação se especificada
        if (notification.actionUrl) {
          window.location.href = notification.actionUrl;
        }
      };

    } catch (error) {
      console.error('Erro ao mostrar notificação:', error);
    }
  }

  async showBatchNotifications(notifications: NotificationData[]): Promise<void> {
    if (!this.isSupported || this.permission !== 'granted') {
      return;
    }

    // Mostrar apenas as notificações mais importantes primeiro
    const urgentNotifications = notifications.filter(n => n.priority === 'urgent');
    const otherNotifications = notifications.filter(n => n.priority !== 'urgent');

    // Mostrar notificações urgentes imediatamente
    for (const notification of urgentNotifications) {
      await this.showNotification(notification);
    }

    // Mostrar outras notificações com delay para não sobrecarregar
    for (let i = 0; i < otherNotifications.length; i++) {
      setTimeout(() => {
        this.showNotification(otherNotifications[i]);
      }, i * 2000); // 2 segundos entre cada notificação
    }
  }

  isPermissionGranted(): boolean {
    return this.permission === 'granted';
  }

  private isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private isStandalonePWA(): boolean {
    // Verifica se o PWA foi adicionado à tela inicial no iOS
    return window.matchMedia('(display-mode: standalone)').matches || 
           (window.navigator as any).standalone === true;
  }

  private isIOSPWACapable(): boolean {
    const isIOS = this.isIOSDevice();
    const isStandalone = this.isStandalonePWA();
    const hasUserActivation = 'userActivation' in navigator; // iOS 16.4+
    
    console.log('[NotificationService] iOS Device:', isIOS);
    console.log('[NotificationService] Standalone PWA:', isStandalone);
    console.log('[NotificationService] Has User Activation (iOS 16.4+):', hasUserActivation);
    
    return isIOS && isStandalone && hasUserActivation;
  }

  getIsSupported(): boolean {
    return this.isSupported;
  }

  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  // Método principal para agendar notificações de calendário
  async scheduleCalendarNotification(
    eventId: string,
    eventTitle: string,
    eventDate: Date,
    eventType: 'appointment' | 'task' | 'deadline' = 'appointment'
  ): Promise<boolean> {
    if (!this.isPermissionGranted()) {
      const granted = await this.requestPermission();
      if (!granted) return false;
    }

    try {
      const { addDays, setHours, setMinutes, isAfter } = await import('date-fns');
      
      // Notificação 1 dia antes às 8h
      const oneDayBefore = addDays(eventDate, -1);
      const reminderTime = setMinutes(setHours(oneDayBefore, 8), 0);
      
      if (isAfter(reminderTime, new Date())) {
        const reminderNotification: ScheduledNotification = {
          id: `${eventId}-reminder`,
          type: eventType,
          title: 'Lembrete de Agenda',
          message: `Amanhã você tem: ${eventTitle}`,
          scheduledFor: reminderTime,
          originalDate: eventDate,
          relatedId: eventId,
          priority: 'normal'
        };
        
        await this.scheduleNotification(reminderNotification);
      }

      // Notificação no dia do evento às 8h
      const eventDayMorning = setMinutes(setHours(eventDate, 8), 0);
      
      if (isAfter(eventDayMorning, new Date())) {
        const eventDayNotification: ScheduledNotification = {
          id: `${eventId}-event`,
          type: eventType,
          title: 'Agenda de Hoje',
          message: `Hoje você tem: ${eventTitle}`,
          scheduledFor: eventDayMorning,
          originalDate: eventDate,
          relatedId: eventId,
          priority: 'high'
        };
        
        await this.scheduleNotification(eventDayNotification);
      }

      return true;
    } catch (error) {
      console.error('Erro ao agendar notificação do calendário:', error);
      return false;
    }
  }

  private async scheduleNotification(notification: ScheduledNotification): Promise<void> {
    console.log('[NotificationService] 📅 Agendando notificação:', notification.id);
    
    const now = new Date();
    const delay = notification.scheduledFor.getTime() - now.getTime();
    
    console.log('[NotificationService] Tempo até notificação (ms):', delay);
    console.log('[NotificationService] Tempo até notificação (min):', Math.round(delay / 60000 * 100) / 100);

    if (delay <= 0) {
      console.warn('[NotificationService] ⚠️ Tentativa de agendar notificação para o passado:', notification);
      return;
    }

    // Armazenar a notificação agendada
    this.scheduledNotifications.set(notification.id, notification);
    console.log('[NotificationService] ✅ Notificação armazenada no Map');

    // Agendar usando setTimeout para notificações próximas (até 24h)
    if (delay <= 24 * 60 * 60 * 1000) {
      console.log('[NotificationService] ⏰ Usando setTimeout (≤24h)');
      const timeoutId = setTimeout(() => {
        console.log('[NotificationService] ⏰ setTimeout disparado, exibindo notificação');
        this.showScheduledNotification(notification);
        this.scheduledNotifications.delete(notification.id);
        this.timeouts.delete(notification.id);
      }, delay);
      
      this.timeouts.set(notification.id, timeoutId);
      console.log('[NotificationService] ✅ setTimeout configurado com ID:', timeoutId);
    } else {
      console.log('[NotificationService] 🔄 Usando background sync (>24h)');
      // Para notificações mais distantes, usar background sync
      this.scheduleWithBackgroundSync(notification);
    }

    console.log(`[NotificationService] ✅ Notificação agendada para: ${notification.scheduledFor.toLocaleString('pt-BR')}`);
  }

  private async scheduleWithBackgroundSync(notification: ScheduledNotification): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        
        // Enviar dados da notificação para o service worker
        if (registration.active) {
          registration.active.postMessage({
            type: 'SCHEDULE_NOTIFICATION',
            notification: {
              ...notification,
              scheduledFor: notification.scheduledFor.toISOString()
            }
          });
        }

        // Tentar registrar background sync para que o SW verifique notificações
        if ('sync' in registration) {
          try {
            await (registration as any).sync.register('check-scheduled-notifications');
            console.log('[NotificationService] background sync registrado para notificações agendadas');
          } catch (syncError) {
            console.warn('[NotificationService] Não foi possível registrar background sync:', syncError);
          }
        }
      } catch (error) {
        console.error('Erro ao agendar com background sync:', error);
      }
    }
  }

  private async showScheduledNotification(notification: ScheduledNotification): Promise<void> {
    if (!this.isPermissionGranted()) {
      console.warn('[NotificationService] Permissão não concedida');
      return;
    }

    console.log('[NotificationService] Exibindo notificação agendada:', notification);
    console.log('[NotificationService] User Agent:', navigator.userAgent);
    
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    
    console.log('[NotificationService] É Android:', isAndroid);
    console.log('[NotificationService] É Mobile:', isMobile);

    try {
      const options: NotificationOptions = {
        body: notification.message,
        icon: '/logoasad.png',
        badge: '/logoasad.png',
        tag: notification.id,
        requireInteraction: notification.priority === 'high' || notification.priority === 'urgent',
        silent: false,
        data: {
          id: notification.id,
          relatedId: notification.relatedId,
          type: notification.type,
          originalDate: notification.originalDate.toISOString()
        }
      };
      
      // Adicionar vibração para Android
      if (isAndroid && 'vibrate' in navigator) {
        (options as any).vibrate = [200, 100, 200];
      }
      
      // Adicionar actions apenas se suportado
      if (!isMobile || 'actions' in Notification.prototype) {
        options.actions = [
          {
            action: 'view',
            title: 'Ver Detalhes'
          },
          {
            action: 'dismiss',
            title: 'Dispensar'
          }
        ];
      }
      
      console.log('[NotificationService] Opções da notificação:', options);

      if ('serviceWorker' in navigator) {
        console.log('[NotificationService] Usando Service Worker');
        const registration = await navigator.serviceWorker.ready;
        console.log('[NotificationService] Service Worker ready:', registration);
        
        await registration.showNotification(notification.title, options);
        console.log('[NotificationService] ✅ Notificação via SW exibida com sucesso');
      } else {
        console.log('[NotificationService] Usando Notification API direta');
        const browserNotification = new Notification(notification.title, options);
        console.log('[NotificationService] ✅ Notificação direta criada');
        
        // Auto-close para notificações não urgentes após 15 segundos
        if (notification.priority !== 'urgent') {
          setTimeout(() => {
            browserNotification.close();
          }, 15000);
        }

        // Adicionar evento de clique
        browserNotification.onclick = () => {
          console.log('[NotificationService] Notificação clicada');
          browserNotification.close();
          
          // Focar na janela do app
          if (window.parent !== window) {
            window.parent.focus();
          } else {
            window.focus();
          }
        };
      }
    } catch (error) {
      console.error('[NotificationService] ❌ Erro ao exibir notificação agendada:', error);
      console.error('[NotificationService] Stack trace:', error.stack);
      
      // Tentar método alternativo para Android
      if (isAndroid) {
        console.log('[NotificationService] Tentando método alternativo para Android...');
        try {
          const simpleNotification = new Notification(notification.title, {
            body: notification.message,
            icon: '/logoasad.png',
            tag: notification.id
          });
          console.log('[NotificationService] ✅ Notificação alternativa criada');
        } catch (altError) {
          console.error('[NotificationService] ❌ Método alternativo também falhou:', altError);
        }
      }
    }
  }

  public async cancelScheduledNotification(notificationId: string): Promise<boolean> {
    try {
      // Cancelar timeout se existir
      const timeoutId = this.timeouts.get(notificationId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.timeouts.delete(notificationId);
      }

      // Remover da lista local
      this.scheduledNotifications.delete(notificationId);

      console.log('Notificação cancelada:', notificationId);
      return true;
    } catch (error) {
      console.error('Erro ao cancelar notificação:', error);
      return false;
    }
  }

  public getScheduledNotifications(): ScheduledNotification[] {
    return Array.from(this.scheduledNotifications.values());
  }

  // Método para agendar lembrete rápido
  public async scheduleQuickReminder(
    title: string,
    message: string,
    minutesFromNow: number,
    priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal'
  ): Promise<string | null> {
    console.log('[NotificationService] 🚀 Iniciando scheduleQuickReminder:', minutesFromNow, 'minutos');
    console.log('[NotificationService] User Agent:', navigator.userAgent);
    console.log('[NotificationService] É Android:', /Android/i.test(navigator.userAgent));
    
    if (!this.isPermissionGranted()) {
      console.log('[NotificationService] Permissão não concedida, solicitando...');
      const granted = await this.requestPermission();
      if (!granted) {
        console.error('[NotificationService] ❌ Permissão de notificação negada');
        return null;
      }
      console.log('[NotificationService] ✅ Permissão concedida');
    } else {
      console.log('[NotificationService] ✅ Permissão já concedida');
    }

    const notificationId = `reminder-${Date.now()}`;
    const scheduledFor = new Date(Date.now() + minutesFromNow * 60 * 1000);

    const notification: ScheduledNotification = {
      id: notificationId,
      type: 'reminder',
      title,
      message,
      scheduledFor,
      originalDate: scheduledFor,
      relatedId: notificationId,
      priority
    };

    console.log('[NotificationService] Notificação criada:', notification);
    console.log('[NotificationService] Tempo agendado:', notification.scheduledFor.toLocaleString());
    console.log('[NotificationService] Tempo atual:', new Date().toLocaleString());
    console.log('[NotificationService] Diferença em ms:', notification.scheduledFor.getTime() - Date.now());

    await this.scheduleNotification(notification);
    console.log('[NotificationService] ✅ scheduleQuickReminder concluído');
    return notificationId;
  }
}

export const notificationService = new NotificationService();
export type { NotificationData, ScheduledNotification };